import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';

const classes = [
  {
    id: 1,
    title: 'Cocktail Fundamentals',
    level: 'Beginner',
    duration: '3 Hours',
    price: 189,
    spots: 8,
    image:
      'https://public.readdy.ai/ai/img_res/8cac8d1d3a2f544273498ff82267782e.jpg',
    description:
      'Master the essential techniques of cocktail making including shaking, stirring, and muddling.',
    includes: ['Welcome drink', 'Recipe booklet', 'Certificate'],
  },
  {
    id: 2,
    title: 'Advanced Mixology',
    level: 'Intermediate',
    duration: '4 Hours',
    price: 289,
    spots: 6,
    image:
      'https://readdy.ai/api/search-image?query=advanced%20cocktail%20masterclass%20with%20sophisticated%20techniques%2C%20molecular%20mixology%2C%20smoke%20infusion%2C%20premium%20spirits%2C%20professional%20bartender%20demonstrating%2C%20elegant%20bar%20atmosphere%2C%20warm%20amber%20lighting&width=400&height=300&seq=class-glam-002&orientation=landscape',
    description:
      'Explore advanced techniques including infusions, syrups, and molecular mixology.',
    includes: ['Premium spirits tasting', 'Advanced manual', 'Bar tools kit'],
  },
  {
    id: 3,
    title: 'Private One-on-One',
    level: 'All Levels',
    duration: '2 Hours',
    price: 449,
    spots: 1,
    image:
      'https://readdy.ai/api/search-image?query=private%20bartending%20lesson%20one%20on%20one%20training%2C%20personal%20cocktail%20instruction%20intimate%20bar%20setting%2C%20professional%20mixologist%20teaching%20student%2C%20focused%20learning%20environment%2C%20luxury%20atmosphere&width=400&height=300&seq=class-glam-003&orientation=landscape',
    description: 'Personalized training tailored to your goals and skill level.',
    includes: ['Customized curriculum', 'Personal recipe book', 'Follow-up support'],
  },
  {
    id: 4,
    title: 'Hen\'s Party Experience',
    level: 'Groups',
    duration: '3 Hours',
    price: 159,
    spots: 12,
    image:
      'https://readdy.ai/api/search-image?query=glamorous%20hens%20party%20bachelorette%20celebration%20with%20women%20making%20colorful%20cocktails%20together%2C%20pink%20and%20gold%20decorations%2C%20champagne%20glasses%2C%20fun%20festive%20atmosphere%2C%20elegant%20bar%20setting%20with%20confetti%20and%20celebration%20vibes%2C%20sophisticated%20ladies%20night%20out&width=400&height=300&seq=class-glam-hens&orientation=landscape',
    description:
      'The ultimate hen\'s party experience! Learn to make signature cocktails with your besties.',
    includes: ['Champagne welcome', 'Party decorations', 'Photo opportunities'],
  },
  {
    id: 5,
    title: 'Home Bar Service',
    level: 'Private',
    duration: '2.5 Hours',
    price: 549,
    spots: 2,
    image:
      'https://readdy.ai/api/search-image?query=romantic%20couple%20learning%20cocktail%20making%20at%20home%20with%20professional%20bartender%20instructor%2C%20intimate%20cozy%20home%20bar%20setting%2C%20elegant%20couple%20enjoying%20mixology%20lesson%20together%2C%20sophisticated%20date%20night%20experience%2C%20warm%20ambient%20lighting&width=400&height=300&seq=class-glam-couples&orientation=landscape',
    description:
      'Bella comes to your home! Perfect for date nights, anniversaries, or learning together.',
    includes: ['In-home service', 'All equipment provided', 'Personalized recipes'],
  },
  {
    id: 6,
    title: 'Corporate Events',
    level: 'Groups',
    duration: '3 Hours',
    price: 119,
    spots: 20,
    image:
      'https://readdy.ai/api/search-image?query=corporate%20team%20building%20cocktail%20event%2C%20business%20professionals%20making%20drinks%20together%2C%20fun%20group%20activity%2C%20modern%20event%20space%2C%20team%20collaboration%2C%20celebratory%20atmosphere%2C%20elegant%20setting&width=400&height=300&seq=class-glam-004&orientation=landscape',
    description:
      'Perfect for team events, parties, and corporate gatherings.',
    includes: ['Group competition', 'Prizes', 'Catering options'],
  },
];

export default function Classes() {
  const [activeClass, setActiveClass] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const { addToCart } = useCart();

  const handleAddToCart = (classItem: typeof classes[0]) => {
    addToCart({
      id: `class-${classItem.id}`,
      name: classItem.title,
      price: classItem.price,
      image: classItem.image,
      type: 'class',
      description: classItem.description,
    });
  };

  return (
    <section id="classes" className="py-32 bg-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-amber-50 to-transparent rounded-full -translate-y-1/2 translate-x-1/3"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-20 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">Learn From The Best</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-display font-normal text-stone-900 mb-5">
            Cocktail Classes
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed">
            From beginner fundamentals to advanced techniques, find the perfect
            class to elevate your mixology skills.
          </p>
        </div>

        {/* Classes Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {classes.map((classItem) => (
            <div
              key={classItem.id}
              className="group bg-white rounded-3xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-2xl hover:border-amber-200/50 transition-all duration-500 cursor-pointer"
              onMouseEnter={() => setActiveClass(classItem.id)}
              onMouseLeave={() => setActiveClass(null)}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={classItem.image}
                  alt={classItem.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent"></div>
                
                {/* Badges */}
                <div className="absolute top-4 left-4">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-xs font-semibold rounded-full shadow-lg">
                    {classItem.level}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-stone-700 text-xs font-medium rounded-full">
                    {classItem.spots} spots
                  </span>
                </div>
                
                {/* Duration Badge */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/90 text-sm">
                  <i className="ri-time-line"></i>
                  <span>{classItem.duration}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="text-xl font-serif font-semibold text-stone-900 mb-2">
                  {classItem.title}
                </h3>
                <p className="text-stone-500 text-sm mb-5 line-clamp-2 leading-relaxed">
                  {classItem.description}
                </p>

                {/* Includes - Expandable */}
                <div
                  className={`space-y-2 mb-5 overflow-hidden transition-all duration-500 ${
                    activeClass === classItem.id
                      ? 'max-h-32 opacity-100'
                      : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    Includes:
                  </p>
                  {classItem.includes.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm text-stone-600"
                    >
                      <i className="ri-check-line text-amber-500"></i>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-5 border-t border-stone-100">
                  <div>
                    <span className="text-3xl font-display text-stone-900">
                      ${classItem.price}
                    </span>
                    <span className="text-stone-400 text-sm ml-1">/person</span>
                  </div>
                  <button
                    onClick={() => handleAddToCart(classItem)}
                    className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-500/20 hover:shadow-lg hover:scale-105 flex items-center gap-2"
                  >
                    <i className="ri-shopping-cart-line"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
